import React, { useState, FormEvent, useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { cn } from '../../lib/utils';
import {
  HsmTemplate,
  HsmTestMessageRequest
} from '../../types';

// Helper to get template body text from components
function getTemplateBodyText(template: HsmTemplate): string {
  const bodyComponent = template.components?.find((c) => c.type === 'BODY');
  return bodyComponent?.text || '';
}

// Helper to extract parameters from template body text
function getTemplateParameters(template: HsmTemplate): string[] {
  const bodyText = getTemplateBodyText(template);
  const matches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
  return matches.map((m) => m.replace(/\{\{|\}\}/g, ''));
}

export interface HsmTestMessageDialogProps {
  // Dialog state
  open: boolean;
  onOpenChange: (open: boolean) => void;

  // Form state
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  templateName: string;
  setTemplateName: (value: string) => void;
  templateParameters: string;
  setTemplateParameters: (value: string) => void;
  sending: boolean;

  // Templates
  approvedTemplates: HsmTemplate[];
  templatesLoading: boolean;

  // Handlers
  onSubmit: (data: HsmTestMessageRequest) => Promise<{ success: boolean; error?: string }>;
  resetForm: () => void;
}

export function HsmTestMessageDialog({
  open,
  onOpenChange,
  phoneNumber,
  setPhoneNumber,
  templateName,
  setTemplateName,
  templateParameters,
  setTemplateParameters,
  sending,
  approvedTemplates,
  templatesLoading,
  onSubmit,
  resetForm
}: HsmTestMessageDialogProps) {
  // Template dropdown state
  const [testTemplateOpen, setTestTemplateOpen] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  // Handler to select template
  const handleSelectTemplate = (template: HsmTemplate) => {
    setTemplateName(template.name);
    const params = getTemplateParameters(template);
    if (params.length > 0) {
      setTemplateParameters(params.map((_, i) => `param${i + 1}`).join(','));
    }
    setTestTemplateOpen(false);
  };

  // Handle form submit
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const params = templateParameters
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    await onSubmit({
      phoneNumber,
      templateName,
      parameters: params.length > 0 ? params : undefined
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Enviar Mensagem de Teste</DialogTitle>
          <DialogDescription>
            Envie uma mensagem de teste para verificar a configuracao.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="test-phone">Telefone (E.164)</Label>
            <Input
              id="test-phone"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+5519971723928"
              required
            />
            <p className="text-xs text-muted-foreground">
              Formato: +[codigo pais][DDD][numero]. Ex: +5519971723928
            </p>
          </div>
          <div className="space-y-2">
            <Label>Template</Label>
            <Popover open={testTemplateOpen} onOpenChange={setTestTemplateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={testTemplateOpen}
                  className="w-full justify-between font-normal"
                >
                  {templateName || 'Selecione um template...'}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar template..." />
                  <CommandList>
                    <CommandEmpty>
                      {templatesLoading ? 'Carregando templates...' : 'Nenhum template encontrado.'}
                    </CommandEmpty>
                    <CommandGroup heading="Templates Aprovados">
                      {approvedTemplates.map((template) => (
                        <CommandItem
                          key={template.id}
                          value={template.name}
                          onSelect={() => handleSelectTemplate(template)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              templateName === template.name ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium">{template.name}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {getTemplateBodyText(template).substring(0, 50)}
                              {getTemplateBodyText(template).length > 50 && '...'}
                            </div>
                          </div>
                          <Badge variant="secondary" className="ml-2 shrink-0">
                            {template.language}
                          </Badge>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">
              Selecione um template aprovado ou digite o nome manualmente.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="test-template">Nome do Template</Label>
            <Input
              id="test-template"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="hello_world"
              required
            />
          </div>
          {/* Template Preview */}
          {templateName && approvedTemplates.find((t) => t.name === templateName) && (
            <div className="p-3 rounded-lg border bg-muted/30 space-y-2">
              <Label className="text-xs text-muted-foreground">Preview do Template</Label>
              <div className="p-2 rounded bg-green-100 dark:bg-green-900/30 text-sm">
                {getTemplateBodyText(approvedTemplates.find((t) => t.name === templateName)!)}
              </div>
              {getTemplateParameters(approvedTemplates.find((t) => t.name === templateName)!).length > 0 && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <span>Parametros necessarios:</span>
                  {getTemplateParameters(approvedTemplates.find((t) => t.name === templateName)!).map((p, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {`{{${p}}}`}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="test-params">Parametros (opcional)</Label>
            <Input
              id="test-params"
              value={templateParameters}
              onChange={(e) => setTemplateParameters(e.target.value)}
              placeholder="param1,param2"
            />
            <p className="text-xs text-muted-foreground">
              Parametros separados por virgula para substituir placeholders no template.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={sending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={sending}>
              {sending ? 'Enviando...' : 'Enviar Teste'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
